/**
 * App — phase orchestration and shared camera ownership.
 *
 *   rite → field → (reflection ⇄ field) → ending → rite
 *
 * There is no onboarding form any more. WinterMute is present from the moment
 * the link opens and asks for each grant itself, one beat at a time — see
 * `rite/script.ts` for why a touch still has to sit between the asking and the
 * native prompt, and why that is not a compromise.
 *
 * The single camera controller is owned here and passed to whichever scene needs
 * it, honouring the one-stream-at-a-time constraint: only one of Rite/Field
 * (back camera) or Reflection (front) holds a stream at a time. The Rite opens
 * the back camera during its own beat, and the Field inherits it still running,
 * so entry is seamless rather than a second permission prompt.
 */

import { useEffect, useRef, useState } from 'react';
import type { SeedFragment } from '@wintermute/orchestrator';
import { useStore } from './state/store.js';
import { runtime } from './session/runtime.js';
import { loadCorpus } from './corpus/loadCorpus.js';
import { useCamera } from './passthrough/useCamera.js';
import { Rite, type EntryState, type RiteGrants } from './rite/Rite.js';
import { Field } from './field/Field.js';
import { FieldHud } from './scenes/FieldHud.js';
import { Reflection } from './scenes/Reflection.js';
import { Ending } from './scenes/Ending.js';
import { ScanOverlay } from './visual/ScanOverlay.js';
import { SlamDebugHud } from './visual/SlamDebugHud.js';
import { WatcherEye } from './visual/Sun/WatcherEye.js';
import { runDemo, type DemoHandle } from './demo/demoScript.js';
import { primeXRSupport } from './spatial/WebXRBackend.js';
import { warmGlyphs } from './visual/Text/glyphs.js';

/** Characters worth having ready before the first fragment lands. */
const WARM =
  'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 .,;:!?—’\'"()-';

export default function App() {
  const phase = useStore((s) => s.phase);
  const setPhase = useStore((s) => s.setPhase);
  const setWatcherConsent = useStore((s) => s.setWatcherConsent);
  const slamHud = useStore((s) => s.showSlamHud);
  const toggleSlamHud = useStore((s) => s.toggleSlamHud);

  const camera = useCamera();
  const [seeds, setSeeds] = useState<SeedFragment[]>([]);
  const demoRef = useRef<DemoHandle | null>(null);
  /** Starting the session takes seconds and can fail; the Rite shows both. */
  const [entry, setEntry] = useState<EntryState>('idle');
  const lastGrants = useRef<RiteGrants | null>(null);
  /** Invalidates an enter() that was superseded (restart, retry, unmount). */
  const enterSeq = useRef(0);

  useEffect(() => {
    void loadCorpus().then(setSeeds);
    // Ask the platform about immersive-ar NOW, so the entry handler can act on
    // the answer synchronously. Awaiting this inside a gesture would consume it.
    void primeXRSupport();
    // Sampling glyphs is a one-off cost of well under a second; paying it during
    // the Rite means the first fragment assembles instead of stuttering.
    warmGlyphs(WARM);
  }, []);

  /**
   * Demo mode moved to `?demo=1` when the onboarding page was replaced by the
   * Rite. It is the deterministic, offline, camera-less run that goes on a panel
   * member's phone, so losing it was not an option — but a visible button would
   * have been the one piece of ordinary UI in an opening that has none. A URL
   * flag keeps the path and leaves the screen clean.
   */
  useEffect(() => {
    if (!seeds.length) return;
    if (!new URLSearchParams(window.location.search).has('demo')) return;
    let cancelled = false;
    setEntry('loading');
    void (async () => {
      try {
        // Idempotent: under StrictMode this effect runs twice, and start() tears
        // the first run down rather than leaking its engine and backend.
        await runtime.start({
          seeds,
          regime: 'crisis-swarm',
          sensingConsent: false,
          watcherConsent: false,
          demo: true,
        });
      } catch (e) {
        console.error('[app] demo start failed', e);
        if (!cancelled) setEntry('failed');
        return;
      }
      if (cancelled) return;
      setEntry('idle');
      setPhase('field');
      demoRef.current?.stop();
      demoRef.current = runDemo(() => setPhase('ending'));
    })();
    return () => {
      cancelled = true;
    };
  }, [seeds, setPhase]);

  /** The Rite finished. `grants` is what the participant actually allowed. */
  const enter = async (grants: RiteGrants) => {
    lastGrants.current = grants;
    const seq = ++enterSeq.current;
    setWatcherConsent(grants.watcher);
    setEntry('loading');
    try {
      // The corpus normally lands long before the Rite ends; if it has not, wait
      // for it rather than opening an empty field.
      const corpus = seeds.length ? seeds : await loadCorpus();
      if (seq !== enterSeq.current) return;
      await runtime.start({
        seeds: corpus,
        regime: 'neutral',
        // Face sensing is a separate, later episode (Reflection), not part of the
        // Rite — the front camera cannot be open while the back one is.
        sensingConsent: false,
        watcherConsent: grants.watcher,
        demo: false,
      });
    } catch (e) {
      console.error('[app] could not start the session', e);
      if (seq !== enterSeq.current) return;
      // Release anything half-started and withdraw the Watcher consent flag: no
      // field is running for it to apply to. Retry sets it again.
      runtime.teardown();
      setWatcherConsent(false);
      setEntry('failed');
      return;
    }
    if (seq !== enterSeq.current) return;
    setEntry('idle');
    setPhase('field');
  };

  const retryEnter = () => {
    // Demo mode has no grants of its own; a reload is the honest retry.
    if (lastGrants.current) void enter(lastGrants.current);
    else window.location.reload();
  };

  const endSession = () => {
    // finish() also stops the Watcher, mutation, exchange and tick loops and
    // discards any request still in flight. The camera and the spatial backend
    // are released by the phase effect below, which also covers the demo run's
    // own route to the ending.
    runtime.finish();
    setPhase('ending');
  };

  // Leaving the field for good: nothing on the Ending screen needs a camera, the
  // tracker, or the Watcher, and a camera that stays on with no eye shown is a
  // consent problem rather than a tidiness one. The engine is kept for export.
  useEffect(() => {
    if (phase !== 'ending') return;
    runtime.release();
    camera.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const restart = () => {
    enterSeq.current++;
    demoRef.current?.stop();
    demoRef.current = null;
    camera.stop();
    runtime.teardown();
    setEntry('idle');
    const s = useStore.getState();
    s.setWatcherConsent(false);
    s.setWatcherLooking(false);
    s.setLive([]);
    s.setDrift(null);
    s.setRhizome(null);
    s.setLastMutation(null);
    s.setLastObservation(null);
    s.setDrawerOpen(false);
    setPhase('rite');
  };

  useEffect(
    () => () => {
      enterSeq.current++;
      demoRef.current?.stop();
      runtime.teardown();
    },
    [],
  );

  return (
    <div className="app">
      {phase === 'rite' && (
        <Rite camera={camera} onEnter={enter} entry={entry} onRetry={retryEnter} />
      )}

      {phase === 'field' && (
        <>
          <Field camera={camera} />
          <ScanOverlay />
          {/* The Watcher's eye sits above the field and below the controls. It
              is the honest "you are being looked at" signal, so it is never
              inside the drawer and never suppressed by the scan toggle. */}
          <WatcherEye />
          <FieldHud onReflect={() => setPhase('reflection')} onEnd={endSession} />
          {slamHud && <SlamDebugHud onClose={toggleSlamHud} />}
        </>
      )}

      {phase === 'reflection' && <Reflection camera={camera} onDone={() => setPhase('field')} />}

      {phase === 'ending' && <Ending onRestart={restart} />}
    </div>
  );
}
